import * as React from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  LineChart
} from "recharts";
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { BoxShadow, Palette, Sizes } from '../@shared/palette';
import { ArrowBtnSx, DateTimeSx, FlexBoxInline, InputBaseSx, SelectMenuItemSx, SelectMenuProps } from '../@shared/templates';
import { useCharts, UseChartsProps } from '../hooks/useCharts';
import { UseReportingResult } from '../hooks/useReporting';
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";



export type LineChartProps = UseChartsProps & UseReportingResult & {
  data: any[];
  title: string;
}

const LineChartComponent = (props: LineChartProps) => {
  const { getCustomTooltip, getCustomDot } = useCharts({ ...props });

  const { baseRequest, onChangeGranularity, onChangeFrom, onChangeTo } = props;


  return <Box sx={{ padding: "10px 40px 0 0", background: Palette.backgroundColorPrimary, borderRadius: "4px", boxShadow: BoxShadow }}>
    <Box sx={{ ...FlexBoxInline, flexDirection: "row-reverse", paddingLeft: "10px", marginBottom: "10px" }}>
      <FormControl sx={{ ...InputBaseSx, maxWidth: "150px" }}>
        <InputLabel id="Granularity">Granularity</InputLabel>
        <Select
          labelId="Granularity"
          size="small"
          value={baseRequest.granularityInMinutes}
          label="Granularity"
          onChange={(e) => onChangeGranularity(e.target.value as number)}
          MenuProps={SelectMenuProps}
        >
          <MenuItem sx={SelectMenuItemSx} value={1440}>1 Day</MenuItem>
          <MenuItem sx={SelectMenuItemSx} value={720}>12 Hours</MenuItem>
          <MenuItem sx={SelectMenuItemSx} value={240}>4 Hours</MenuItem>
          <MenuItem sx={SelectMenuItemSx} value={60}>1 Hour</MenuItem>
          <MenuItem sx={SelectMenuItemSx} value={30}>30 Minutes</MenuItem>
        </Select>

      </FormControl>
      <Box sx={DateTimeSx}>
        <DatePicker
          selected={baseRequest.to}
          customInput={
            <TextField
              label="To"
              variant="outlined"
              size="small"
              sx={{ ...InputBaseSx, maxWidth: "100px" }}
            />
          }
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
              <IconButton sx={ArrowBtnSx} size="small" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                <ArrowBackIosNew fontSize="inherit" />
              </IconButton>
              <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>
                {date.toLocaleString("default", { month: "long" })} {date.getFullYear()}
              </Typography>
              <IconButton sx={ArrowBtnSx} size="small" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                <ArrowForwardIos fontSize="inherit" />
              </IconButton>
            </Stack>
          )}
          onChange={(date: Date | null) => date && onChangeTo(date)} />
      </Box>
      <Box sx={DateTimeSx}>
        <DatePicker
          selected={baseRequest.from}

          customInput={
            <TextField
              label="From"
              variant="outlined"
              size="small"
              sx={{ ...InputBaseSx, maxWidth: "100px" }}
            />
          }
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", px: 1 }}>
              <IconButton sx={ArrowBtnSx} size="small" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                <ArrowBackIosNew fontSize="inherit" />
              </IconButton>
              <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>
                {date.toLocaleString("default", { month: "long" })} {date.getFullYear()}
              </Typography>
              <IconButton sx={ArrowBtnSx} size="small" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                <ArrowForwardIos fontSize="inherit" />
              </IconButton>
            </Stack>
          )}
          onChange={(date: Date | null) => date && onChangeFrom(date)} />
      </Box>
      <Typography sx={{ fontSize: Sizes.pageThirdTitle, color: Palette.textColorPrimary, marginRight: "10px" }}>{props.title}:</Typography>

    </Box>
    <ResponsiveContainer width={"100%"} height={400}>
      <LineChart data={props.data}>
        <CartesianGrid strokeDasharray="3 3" stroke={Palette.borderColorPrimary} />
        <XAxis dataKey="name" tick={{ fontSize: Sizes.textSecondary, fill: Palette.textColorSecondary }} />
        <YAxis tick={{ fontSize: Sizes.textSecondary, fill: Palette.textColorSecondary }} />
        <Tooltip content={getCustomTooltip} />
        {
          props.items.map((item, index) => {
            return <Line
              key={index}
              type="monotone"
              dataKey={item.key}
              stroke={item.color}
              dot={getCustomDot(index)}
              strokeWidth={2}
              name={item.label}
            />
          })
        }
      </LineChart>
    </ResponsiveContainer>
  </Box>
}

export default LineChartComponent;
